/**
 * Pure scene synthesizer — projects per-row plan / binding / intent
 * inputs into a `RenderScene`. Same inputs ⇒ identical scene ⇒
 * identical diff. No DOM, no async, no provider lookups (callers
 * pre-resolve `bindings` and `intents` so the builder is a strict
 * data shuffle).
 *
 * `buildScene` lives next to `RenderPlan.makeForRows` in the
 * dependency graph: callers compute plans per column, resolve
 * bindings + intents, then hand the bundle here. DOM and CLI
 * adapters share the same call site shape — only the reconciler
 * stage diverges.
 */

import type { Option } from "../core/option.ts";
import type { NaiveDate } from "@tento-chrono";
import { ArtifactFreeze } from "@tento-core/artifact-freeze";

import type { CellLayout, ColumnKey, Timescale } from "./layout-types.ts";
import type { FixedChildIntent } from "../providers/plugin-providers.ts";
import type { RenderBindings } from "./render-plugin-core.ts";
import type { RenderPlan } from "./render-plan.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import {
  type CellContent,
  type CellNode,
  type LegendNode,
  type RenderScene,
  type RowOverlayNode,
  type RowSection,
  type SceneBuildCounters,
  SceneKey,
  type SceneNodeStamp,
  type SceneRevision,
} from "./scene.ts";
import { elementAt } from "../core/checked-index.ts";

/**
 * One row's worth of resolved inputs. The caller has already:
 *   - Computed `plansByColumn` via `RenderPlan.makeForRows` per column.
 *   - Resolved `bindings` via `RenderBinding.resolveAll(row, registry)`.
 *   - Collected `intents` via `bindings.cell` plugin's `dataProvider`.
 *
 * `buildScene` does no further resolution — it stitches the bundle
 * into stable-keyed scene nodes.
 *
 * **Immutable once handed over.** An incremental build carries a row's
 * nodes forward when it is given the same input object as the prior
 * build (identity, not content), and the diff skips it the same way. A
 * caller with a changed row passes a new object; one that mutated an
 * input in place (its maps, intents or bindings) would get stale nodes
 * and an empty diff. Under `ArtifactFreeze` (test harnesses) a built
 * input is sealed: its object, intents and bindings are frozen, and its
 * maps throw on `set` / `delete` / `clear`.
 */
export interface SceneRowInput<K extends ColumnKey = ColumnKey> {
  readonly rowId: RowLocalId;
  readonly rowIndex: number;
  readonly label: string;
  readonly bindings: RenderBindings;
  readonly plansByColumn: ReadonlyMap<K, RenderPlan<K>>;
  readonly intents: readonly FixedChildIntent[];
  /**
   * Row's own timescale. Required for owner-period detection in
   * spanned rows and stacked-subcell labeling. Callers that don't
   * know (e.g. plan-inspection adapters) may set it to the sheet's
   * timescale; `buildScene` falls back to `ctx.timescale` when
   * absent.
   */
  readonly timescaleSetting?: Timescale | "custom";
  /**
   * Whether the row is system-synthesized. Defaults to `false` for
   * back-compat with adapters that don't know.
   */
  readonly isSystemRow?: boolean;
  /**
   * Sheet section the row belongs to. Defaults to `"body"`.
   */
  readonly section?: RowSection;
  /**
   * Per-column resolved cell content. Optional — adapters that
   * don't need value-level rendering (e.g. CLI plan inspection)
   * can omit it. When provided, populated entries flow into each
   * cell node's `content` field.
   */
  readonly contentByColumn?: ReadonlyMap<K, CellContent>;
}

/**
 * The pre-resolved inputs `Scene.build` projects into a `RenderScene`: the column range, the time
 * scale and each row's plan, bindings and intents.
 */
export interface SceneBuildInputs<K extends ColumnKey = ColumnKey> {
  readonly columnRange: NaiveDate.Range;
  readonly timescale: Timescale;
  readonly columns: readonly K[];
  readonly rows: readonly SceneRowInput<K>[];
  /**
   * Frame revision to stamp onto the built scene. `Option` — absent for legacy
   * / CLI / TUI callers (the scene's `revision` stays absent, zero behavior
   * change). Injected by the live dual-run per capture.
   */
  readonly revision?: SceneRevision;
  /**
   * Prior scene for by-reference node reuse (structural sharing, the
   * `GeometrySnapshot` precedent). `Option` — `null` on the first build. Reuse
   * only fires when the fresh node carries a stamp whose `(valuesVersion,
   * attrVersion)` pair matches the prior node's AND the structural arms are
   * unchanged; absent stamps ⇒ no reuse (every cell rebuilt = today's behavior).
   */
  readonly prior?: RenderScene<K>;
  /**
   * Per-row output-stamp provider (the L5 `currentVersions` probe). `Option` —
   * absent ⇒ no stamps, no reuse, no ledger reads (exactly today's behavior).
   * A `null` return for one row (placement-only / unresolvable) leaves that
   * cell node stampless.
   */
  readonly stampProvider?: (rowId: RowLocalId) => Option<SceneNodeStamp>;
  /**
   * What lets a node of `prior` be reused by reference:
   * - `"stamps"` (the default): a cell is reused only under matching output
   *   stamps (see `prior`); stampless cells, legends and overlays are rebuilt.
   * - `"inputs"`: a node is the prior node, by reference, exactly when it is
   *   built from the same inputs, and a new object otherwise:
   *   - cell: same key, `rowIndex`, `columnIndex`, column date, `bindings`
   *     and `plan` objects; `content` the same object, or the same `entry`
   *     object with equal glyph flags; equal stamps (or none);
   *   - legend: same key, `rowIndex`, `bindings` object, label, timescale,
   *     system flag and section;
   *   - row overlay: same key, `rowIndex`, `bindings` object, `intents` array.
   *   For a caller that carries untouched rows' and cells' inputs across
   *   frames (an incremental frame): an untouched cell keeps its node, so a
   *   renderer may cache output by node identity; a re-planned cell (resize,
   *   clock, span) or one whose content was re-read gets a new node — what a
   *   value edit does to every cell of the edited column. A reused node is
   *   never stale, since every input it is built from is the same.
   */
  readonly reuse?: "stamps" | "inputs";
}

function buildScene<K extends ColumnKey = ColumnKey>(
  ctx: SceneBuildInputs<K>,
): RenderScene<K> {
  return buildSceneWith(ctx, true);
}

/**
 * `buildScene`; `incremental` allows carrying rows from `prior` (see below).
 * An incremental build gives the same scene as a full one, map order
 * included: it carries only when the rows come in the prior's order with no
 * new row, and starts over in full when a changed row would add a node the
 * prior did not have (an append would move it out of row order).
 */
function buildSceneWith<K extends ColumnKey>(
  ctx: SceneBuildInputs<K>,
  incremental: boolean,
): RenderScene<K> {
  const priorScene = ctx.prior ?? null;
  const byInputs = ctx.reuse === "inputs" && priorScene != null;
  // An incremental build: the prior was built by inputs over the same columns,
  // range and timescale, and no stamp can move under a carried row. Its maps
  // are copied whole (`copyOf`) and only rows whose input object changed are rebuilt.
  const priorInputs = incremental && byInputs ? priorScene?.work?.inputs ?? null : null;
  const carry = priorInputs != null && priorScene != null && ctx.stampProvider == null &&
      priorScene.timescale === ctx.timescale && priorScene.columnRange.start.dse === ctx.columnRange.start.dse &&
      priorScene.columnRange.end.dse === ctx.columnRange.end.dse && sameColumns(priorInputs.columns, ctx.columns) &&
      sameRowOrder(priorInputs.rows, ctx.rows)
    ? priorInputs.rows
    : null;
  const carried = carry != null && priorScene != null;
  const cells: Map<string, CellNode<K>> = carried ? copyOf(priorScene.cells) : new Map();
  const rowOverlays: Map<string, RowOverlayNode> = carried ? copyOf(priorScene.rowOverlays) : new Map();
  const legends: Map<string, LegendNode> = carried ? copyOf(priorScene.legends) : new Map();
  const inputRows = new Map<RowLocalId, object>();

  const priorCells = priorScene?.cells ?? null;
  const priorOverlays = byInputs ? priorScene?.rowOverlays ?? null : null;
  const priorLegends = byInputs ? priorScene?.legends ?? null : null;
  let nodesBuilt = 0;
  let nodesReused = 0;
  let ledgerReads = 0;
  let rowsBuilt = 0;
  let rowsCarried = 0;

  if (carry != null) {
    const present = new Set<RowLocalId>();
    for (const r of ctx.rows) present.add(r.rowId);
    // A row gone since the prior: its nodes leave the copied maps.
    for (const id of carry.keys()) if (!present.has(id)) dropRow(id, ctx.columns, cells, rowOverlays, legends);
  }

  for (const r of ctx.rows) {
    inputRows.set(r.rowId, r);
    if (ArtifactFreeze.isEnabled()) sealRowInput(r);
    if (carry != null) {
      if (carry.get(r.rowId) === r) {
        rowsCarried += 1;
        continue;
      }
    }
    rowsBuilt += 1;
    // One stamp read per row (its own `data:` output node), shared across all
    // that row's column cells — the row's cells share one output version.
    let stamp: Option<SceneNodeStamp> = null;
    if (ctx.stampProvider) {
      stamp = ctx.stampProvider(r.rowId);
      ledgerReads += 1;
    }

    for (let i = 0; i < ctx.columns.length; i++) {
      const column = elementAt(ctx.columns, i, "scene-build: ctx.columns");
      const plan = r.plansByColumn.get(column);
      if (!plan) {
        // A column a changed row no longer plans: its carried node goes.
        if (carry != null) cells.delete(SceneKey.toString(makeCellKey(r.rowId, column)));
        continue;
      }
      const cellKey = makeCellKey(r.rowId, column);
      const keyStr = SceneKey.toString(cellKey);
      if (carry != null && !cells.has(keyStr)) return buildSceneWith(ctx, false);
      const fresh: CellNode<K> = {
        key: cellKey,
        rowIndex: r.rowIndex,
        columnIndex: i,
        columnDate: column.pd.start,
        bindings: r.bindings,
        plan,
        layout: plan.layout,
        content: r.contentByColumn?.get(column) ?? null,
        stamp,
      };
      const prior = priorCells?.get(keyStr) ?? null;
      const reusable = prior != null && (
        byInputs ? sameCellInputs(prior, fresh) : stamp != null && reusableCellNode(prior, fresh)
      );
      if (reusable) {
        cells.set(keyStr, prior);
        nodesReused += 1;
      } else {
        cells.set(keyStr, fresh);
        nodesBuilt += 1;
      }
    }

    if (carry != null) {
      // The overlay the row had: kept in place only when it still has one under the same plugin.
      const had = legends.get(SceneKey.toString(makeLegendKey(r.rowId)));
      const hadKey = had == null ? null : SceneKey.toString(makeOverlayKey(r.rowId, had.bindings.cell.pluginId));
      const nowKey = r.intents.length > 0 ? SceneKey.toString(makeOverlayKey(r.rowId, r.bindings.cell.pluginId)) : null;
      if (nowKey != null && (hadKey !== nowKey || !rowOverlays.has(nowKey))) return buildSceneWith(ctx, false);
      if (nowKey == null && hadKey != null) rowOverlays.delete(hadKey);
    }
    if (r.intents.length > 0) {
      const overlayKey = makeOverlayKey(r.rowId, r.bindings.cell.pluginId);
      const overlayStr = SceneKey.toString(overlayKey);
      const priorOverlay = priorOverlays?.get(overlayStr) ?? null;
      rowOverlays.set(
        overlayStr,
        priorOverlay != null && priorOverlay.rowIndex === r.rowIndex && priorOverlay.bindings === r.bindings &&
          priorOverlay.intents === r.intents
          ? priorOverlay
          : { key: overlayKey, rowIndex: r.rowIndex, bindings: r.bindings, intents: r.intents },
      );
    }

    const legendKey = makeLegendKey(r.rowId);
    const legendStr = SceneKey.toString(legendKey);
    const legend: LegendNode = {
      key: legendKey,
      rowIndex: r.rowIndex,
      bindings: r.bindings,
      label: r.label,
      timescaleSetting: r.timescaleSetting ?? ctx.timescale,
      isSystemRow: r.isSystemRow ?? false,
      section: r.section ?? "body",
    };
    const priorLegend = priorLegends?.get(legendStr) ?? null;
    legends.set(legendStr, priorLegend != null && sameLegend(priorLegend, legend) ? priorLegend : legend);
  }

  // Every node is built or reused; a carried row's nodes are reused without being visited.
  nodesReused = cells.size - nodesBuilt;
  const inputs = ctx.reuse === "inputs" && ctx.stampProvider == null ? { columns: ctx.columns, rows: inputRows } : null;
  const work: SceneBuildCounters = { nodesBuilt, nodesReused, ledgerReads, rowsBuilt, rowsCarried, inputs };
  const scene: RenderScene<K> = {
    columnRange: ctx.columnRange,
    timescale: ctx.timescale,
    cells,
    rowOverlays,
    legends,
    work,
    revision: ctx.revision ?? null,
  };
  // Flowing-message immutability (engine-spec §3). No-op unless a test harness
  // enabled it; `cells`/`rowOverlays`/`legends` are `Map`s that `deep` skips,
  // so live plan/binding objects inside them are never frozen in prod.
  return ArtifactFreeze.deep(scene);
}

/**
 * A copy of a prior scene's map, in its order. Copied entry by entry
 * (`forEach`), not by `new Map(prior)`: the constructor walks the prior with
 * its iterator, which makes a `[key, value]` pair per entry, and at 5000 rows
 * x 32 columns that walk was half of an incremental build's allocation
 * (tui-to-sdk-43: 29.9 MB a copy, 14.0 MB entry by entry).
 */
function copyOf<V>(prior: ReadonlyMap<string, V>): Map<string, V> {
  const copy = new Map<string, V>();
  prior.forEach((value, key) => copy.set(key, value));
  return copy;
}

/**
 * Whether `rows` are the prior build's rows (`prior`, in its order) less any
 * that left, in that order, with none new: the rows an incremental build can
 * carry and keep in place.
 */
function sameRowOrder<K extends ColumnKey>(prior: ReadonlyMap<RowLocalId, object>, rows: readonly SceneRowInput<K>[]): boolean {
  const present = new Set<RowLocalId>();
  for (const r of rows) present.add(r.rowId);
  let i = 0;
  for (const id of prior.keys()) {
    if (!present.has(id)) continue;
    if (rows[i]?.rowId !== id) return false;
    i++;
  }
  return i === rows.length;
}

/** Whether two builds' columns are the same columns, in order (the same objects). */
function sameColumns(a: readonly ColumnKey[], b: readonly ColumnKey[]): boolean {
  return a === b || (a.length === b.length && a.every((column, i) => column === b[i]));
}

/**
 * Remove row `id`'s nodes from the maps a build copied from its prior: a cell
 * per column, its legend, and the overlay its legend's binding names.
 */
function dropRow<K extends ColumnKey>(
  id: RowLocalId,
  columns: readonly K[],
  cells: Map<string, CellNode<K>>,
  rowOverlays: Map<string, RowOverlayNode>,
  legends: Map<string, LegendNode>,
): void {
  for (const column of columns) cells.delete(SceneKey.toString(makeCellKey(id, column)));
  const legendStr = SceneKey.toString(makeLegendKey(id));
  const legend = legends.get(legendStr);
  if (legend != null) rowOverlays.delete(SceneKey.toString(makeOverlayKey(id, legend.bindings.cell.pluginId)));
  legends.delete(legendStr);
}

/**
 * Whether the `fresh` cell node may reuse `prior` by reference. Version
 * fast-path: an equal stamped `(valuesVersion, attrVersion)` pair ⇒ the row's
 * output content AND attribute bag are identical, so only the structural arms
 * the versions do NOT cover (position / geometry / span / binding) need
 * checking — the same arms the diff classifies outside `contentChanged`. An
 * attribute-only mutation moves `attrVersion` (not `valuesVersion`), so this
 * check MUST include it: reusing across an attr bump would hand the diff two
 * identical-by-reference nodes and mask the change. Absent stamps never reuse.
 */
function reusableCellNode<K extends ColumnKey>(
  prior: CellNode<K>,
  fresh: CellNode<K>,
): boolean {
  const ps = prior.stamp;
  const fs = fresh.stamp;
  if (ps == null || fs == null) return false;
  if (ps.valuesVersion !== fs.valuesVersion) return false;
  if (ps.attrVersion !== fs.attrVersion) return false;
  // The clock can change a cell's appearance without a row mutation.
  if (
    prior.plan.timeState.inThePast !== fresh.plan.timeState.inThePast ||
    prior.plan.timeState.isCurrent !== fresh.plan.timeState.isCurrent ||
    prior.plan.timeState.isFuture !== fresh.plan.timeState.isFuture
  ) return false;
  if (prior.rowIndex !== fresh.rowIndex) return false;
  if (prior.columnIndex !== fresh.columnIndex) return false;
  if (prior.plan.spanMode.type !== fresh.plan.spanMode.type) return false;
  if (prior.plan.descriptorType !== fresh.plan.descriptorType) return false;
  if (prior.bindings.cell.pluginId !== fresh.bindings.cell.pluginId) return false;
  if (prior.bindings.cell.pluginRole !== fresh.bindings.cell.pluginRole) {
    return false;
  }
  return cellLayoutEqual(prior.layout, fresh.layout);
}

/**
 * `reuse: "inputs"` for a cell: the same key (the caller looked `prior` up
 * by it), position, column date, bindings and plan objects, content that is
 * the same object or carries the same entry object and glyph flags, and the
 * same stamp (both absent, or an equal `(valuesVersion, attrVersion)`). The
 * plan carries the layout, span mode and clock state, so a re-plan, a resize
 * or a clock tick that makes a new plan makes a new node.
 */
function sameCellInputs<K extends ColumnKey>(prior: CellNode<K>, fresh: CellNode<K>): boolean {
  if (prior.plan !== fresh.plan || prior.bindings !== fresh.bindings) return false;
  if (prior.rowIndex !== fresh.rowIndex || prior.columnIndex !== fresh.columnIndex) return false;
  if (prior.columnDate.dse !== fresh.columnDate.dse) return false;
  const ps = prior.stamp;
  const fs = fresh.stamp;
  if ((ps == null) !== (fs == null)) return false;
  if (ps != null && fs != null && (ps.valuesVersion !== fs.valuesVersion || ps.attrVersion !== fs.attrVersion)) {
    return false;
  }
  return sameContent(prior.content, fresh.content);
}

function sameContent(a: Option<CellContent>, b: Option<CellContent>): boolean {
  if (a === b) return true;
  if (a == null || b == null || a.kind !== b.kind) return false;
  if (a.kind === "none" || b.kind === "none") return true;
  if (a.entry !== b.entry) return false;
  const ag = a.glyphs;
  const bg = b.glyphs;
  if (ag === bg) return true;
  return (ag?.formula ?? false) === (bg?.formula ?? false) && (ag?.syncing ?? false) === (bg?.syncing ?? false) &&
    (ag?.stale ?? false) === (bg?.stale ?? false) && (ag?.external ?? false) === (bg?.external ?? false);
}

function sameLegend(a: LegendNode, b: LegendNode): boolean {
  return a.rowIndex === b.rowIndex && a.bindings === b.bindings && a.label === b.label &&
    a.timescaleSetting === b.timescaleSetting && a.isSystemRow === b.isSystemRow && a.section === b.section;
}

/** Field-wise layout equality (the geometry arm of node reuse). */
function cellLayoutEqual(
  a: Option<CellLayout>,
  b: Option<CellLayout>,
): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.cellHeightPx !== b.cellHeightPx) return false;
  if (a.cellHeightWithChildrenPx !== b.cellHeightWithChildrenPx) return false;
  if (a.offsetPx !== b.offsetPx) return false;
  return (a.stackingInfo?.cellsPerColumn ?? null) ===
    (b.stackingInfo?.cellsPerColumn ?? null);
}

function makeCellKey<K extends ColumnKey>(
  row: RowLocalId,
  column: K,
): SceneKey & { readonly kind: "cell" } {
  return { kind: "cell", row, column };
}

function makeOverlayKey(
  row: RowLocalId,
  pluginId: string,
): SceneKey & { readonly kind: "row-overlay" } {
  return { kind: "row-overlay", row, pluginId };
}

function makeLegendKey(
  row: RowLocalId,
): SceneKey & { readonly kind: "legend" } {
  return { kind: "legend", row };
}

/**
 * Builds a `RenderScene` from per-row plans, bindings and intents; render front-ends and the CLI
 * diff two scenes to find what to repaint.
 */
export namespace Scene {
  export const build = buildScene;
}

/** The error a sealed scene input's map throws on a write (`SceneRowInput`: immutable once handed over). */
export class SceneInputMutated extends Error {
  constructor(rowId: RowLocalId, field: string, verb: string) {
    super(`SceneRowInput of row ${rowId.toString()}: ${field}.${verb} after the input was handed to a build`);
    this.name = "SceneInputMutated";
  }
}

const sceneInputMapSeal = Symbol("SceneRowInput sealed map");

/** Seal `input` (`ArtifactFreeze` on): freeze it, its intents and bindings, and make its maps refuse writes. */
function sealRowInput(input: SceneRowInput): void {
  if (Object.isFrozen(input)) return;
  for (const [field, map] of [["plansByColumn", input.plansByColumn], ["contentByColumn", input.contentByColumn]] as const) {
    if (map == null || Object.getOwnPropertyDescriptor(map, sceneInputMapSeal)?.value === sceneInputMapSeal) continue;
    for (const verb of ["set", "delete", "clear"] as const) {
      Object.defineProperty(map, verb, {
        value: () => {
          throw new SceneInputMutated(input.rowId, field, verb);
        },
      });
    }
    Object.defineProperty(map, sceneInputMapSeal, { value: sceneInputMapSeal });
  }
  Object.freeze(input.intents);
  Object.freeze(input.bindings);
  Object.freeze(input);
}
